import axios from "axios";
import { useEffect, useMemo, useState, useCallback } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./UserManagement.module.css";

const BASE_URL = "https://prod.toxboxai.com";

// Role normalization
const normalizeRole = (r) => {
  if (!r) return "";
  const low = String(r).toLowerCase();
  if (low === "owner") return "Owner";
  if (low === "subowner" || low === "sub_owner" || low === "subadmin") return "SubOwner";
  if (low === "vendor" || low === "client") return "Vendor";
  if (low === "participant" || low === "salesperson" || low === "sales") return "Participant";
  return r;
};

const roleLabels = {
  vendor: "Client",
  participant: "Sales Person",
  owner: "Admin",
  subowner: "Sub Admin",
};

const getRoleDisplayName = (role) => {
  if (!role) return "User";
  return roleLabels[String(role).toLowerCase()] || role;
};

const capitalizeWords = (s) =>
  s
    .split(" ")
    .filter(Boolean)
    .map((x) => x.charAt(0).toUpperCase() + x.slice(1).toLowerCase())
    .join(" ");

const UserManagement = () => {
  const [showFormPopup, setShowFormPopup] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [jwtToken, setJwtToken] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const [displayUsers, setDisplayUsers] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    permissions: [],
    role: "",
    domainType: "",
  });
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterRole, setFilterRole] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [totalPages, setTotalPages] = useState(1);
  const [locations, setLocations] = useState([]);
  const [locationsInput, setLocationsInput] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionBusyId, setActionBusyId] = useState(null);
  const [errors, setErrors] = useState({}); // For inline validation messages

  // Derived
  const lsRoleRaw = useMemo(() => localStorage.getItem("role") || "", []);
  const lsRoleLabel = useMemo(
    () => roleLabels[lsRoleRaw?.toLowerCase()] || "Not logged in",
    [lsRoleRaw]
  );

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${jwtToken}`,
      "Content-Type": "application/json",
    }),
    [jwtToken]
  );

  const getDefaultRoleForUser = () => {
    const r = normalizeRole(userRole);
    if (r === "Owner") return "SubOwner";
    if (r === "SubOwner") return "Vendor";
    if (r === "Vendor") return "Participant";
    return "";
  };

  // Real-time validation
  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case "name":
        if (!value.trim()) newErrors.name = "Name is required";
        else delete newErrors.name;
        break;
      case "email":
        if (!value.trim()) newErrors.email = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
          newErrors.email = "Enter a valid email address";
        else delete newErrors.email;
        break;
      case "password":
        if (!selectedUserId && !value) {
          newErrors.password = "Password is required";
        } else if (value && value.length < 8) {
          newErrors.password = "Password must be at least 8 characters";
        } else if (value && !/(?=.*[a-z])/.test(value)) {
          newErrors.password = "Must contain a lowercase letter";
        } else if (value && !/(?=.*[A-Z])/.test(value)) {
          newErrors.password = "Must contain an uppercase letter";
        } else if (value && !/(?=.*\d)/.test(value)) {
          newErrors.password = "Must contain a number";
        } else if (value && !/(?=.*[@$!%*?&])/.test(value)) {
          newErrors.password = "Must contain a special character (@$!%*?&)";
        } else {
          delete newErrors.password;
        }
        break;
      case "role":
        if ((normalizeRole(userRole) === "Owner" || normalizeRole(userRole) === "SubOwner") && !value) {
          newErrors.role = "Role is required";
        } else {
          delete newErrors.role;
        }
        break;
      case "domainType":
        if (normalizeRole(formData.role) === "Vendor" && !value) {
          newErrors.domainType = "Domain Type is required for Client";
        } else {
          delete newErrors.domainType;
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const handleLocationsChange = (e) => {
    const input = e.target.value;
    setLocationsInput(input);
    const formatted = input
      .split(",")
      .map((loc) => capitalizeWords(loc.trim()))
      .filter((x) => x.length > 0);
    setLocations(formatted);

    if (normalizeRole(userRole) === "Vendor" && normalizeRole(formData.role) === "Participant") {
      if (formatted.length === 0 && input.trim() !== "") {
        setErrors((prev) => ({ ...prev, locations: "Enter at least one valid location" }));
      } else {
        setErrors((prev) => {
          const { locations, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "permissions") {
      const parsed = value.split(",").map((p) => p.trim()).filter(Boolean);
      setFormData((prev) => ({ ...prev, permissions: parsed }));
    } else if (name === "name") {
      if (/^[A-Za-z ]*$/.test(value)) {
        setFormData((prev) => ({ ...prev, name: value }));
        validateField(name, value);
      }
    } else if (name === "role") {
      setFormData((prev) => ({ ...prev, role: normalizeRole(value) }));
      validateField(name, value);
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      validateField(name, value);
    }
  };

  const safeToast = (msg, type = "error") =>
    type === "success" ? toast.success(msg) : toast.error(msg);

  const handleAxiosError = (err, fallback = "Operation failed") => {
    if (!err?.response) return safeToast("Network error - please check your connection");
    const msg = err.response?.data?.message || err.response?.data?.error || fallback;
    safeToast(msg);
  };

  const fetchUsers = useCallback(async (role, token, roleFilter = "") => {
    if (!token || !role) return;
    setLoadingList(true);
    try {
      const r = normalizeRole(role);
      let responseData = [];

      if (r === "Owner") {
        const [subRes, venRes] = await Promise.all([
          axios.get(`${BASE_URL}/api/owner/subowners`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${BASE_URL}/api/owner/vendors`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        const subOwners = subRes.data?.data || subRes.data?.users || subRes.data || [];
        const vendors = venRes.data?.data || venRes.data?.users || venRes.data || [];
        responseData = [...subOwners, ...vendors];
      } else if (r === "SubOwner") {
        const res = await axios.get(`${BASE_URL}/api/owner/vendors`, { headers: { Authorization: `Bearer ${token}` } });
        responseData = res.data?.data || res.data?.users || res.data || [];
      } else if (r === "Vendor") {
        const res = await axios.get(`${BASE_URL}/api/vendor/participants`, { headers: { Authorization: `Bearer ${token}` } });
        responseData = res.data?.data || res.data?.users || res.data || [];
      }

      const filtered = roleFilter
        ? responseData.filter((u) => normalizeRole(u.role) === normalizeRole(roleFilter))
        : responseData;

      setAllUsers(filtered);
      setTotalPages(Math.max(1, Math.ceil(filtered.length / itemsPerPage)));
      const start = (currentPage - 1) * itemsPerPage;
      setDisplayUsers(filtered.slice(start, start + itemsPerPage));
    } catch (err) {
      handleAxiosError(err, "Failed to load users");
      setAllUsers([]);
      setDisplayUsers([]);
    } finally {
      setLoadingList(false);
    }
  }, [itemsPerPage, currentPage]);

  useEffect(() => {
    const token = localStorage.getItem("token") || "";
    const role = normalizeRole(localStorage.getItem("role") || "");

    if (!token || !["Owner", "SubOwner", "Vendor"].includes(role)) {
      toast.error("Please log in to access user management.");
      return;
    }

    setJwtToken(token);
    setUserRole(role);
    fetchUsers(role, token, filterRole);
  }, [fetchUsers, filterRole]);

  useEffect(() => {
    const start = (currentPage - 1) * itemsPerPage;
    setDisplayUsers(allUsers.slice(start, start + itemsPerPage));
  }, [currentPage, allUsers, itemsPerPage]);

  const openAddForm = () => {
    setSelectedUserId(null);
    setSelectedUser(null);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: getDefaultRoleForUser(),
      permissions: [],
      domainType: "",
    });
    setLocations([]);
    setLocationsInput("");
    setErrors({});
    setShowFormPopup(true);
  };

  const handleEditClick = (user) => {
    setSelectedUser(user);
    setSelectedUserId(user._id);
    setFormData({
      name: user.name || "",
      email: user.email || "",
      password: "",
      role: normalizeRole(user.role || ""),
      permissions: user.permissions || [],
      domainType: user.domainType || "",
    });
    const locs = Array.isArray(user.locations) ? user.locations : [];
    setLocations(locs);
    setLocationsInput(locs.join(", "));
    setErrors({});
    setShowFormPopup(true);
  };

  const closeForm = () => {
    setShowFormPopup(false);
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "",
      permissions: [],
      domainType: "",
    });
    setLocations([]);
    setLocationsInput("");
    setSelectedUserId(null);
    setErrors({});
  };

  const hasErrors = () => Object.keys(errors).length > 0;

  const buildCreatePayload = () => {
    const r = normalizeRole(userRole);
    const roleToCreate = normalizeRole(formData.role);
    const common = {
      name: capitalizeWords(formData.name.trim()),
      email: formData.email.trim().toLowerCase(),
      approved: true,
    };

    if (r === "Owner" && roleToCreate === "SubOwner") {
      return {
        url: `${BASE_URL}/api/owner/create-subowner`,
        payload: { ...common, password: formData.password, permissions: formData.permissions },
      };
    }
    if ((r === "Owner" || r === "SubOwner") && roleToCreate === "Vendor") {
      return {
        url: `${BASE_URL}/api/auth/register/vendor`,
        payload: { ...common, password: formData.password, domainType: formData.domainType },
      };
    }
    if (r === "Vendor" && roleToCreate === "Participant") {
      return {
        url: `${BASE_URL}/api/auth/register/participant`,
        payload: { ...common, password: formData.password, role: "Participant", locations },
      };
    }
    return null;
  };

  const handleCreate = async () => {
    if (hasErrors()) return;
    setSubmitting(true);
    try {
      const config = buildCreatePayload();
      if (!config) throw new Error("Invalid role selection");
      await axios.post(config.url, config.payload, { headers: authHeaders });
      safeToast(`${getRoleDisplayName(formData.role)} created successfully`, "success");
      closeForm();
      await fetchUsers(userRole, jwtToken, filterRole);
    } catch (err) {
      handleAxiosError(err, "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (userId) => {
    if (hasErrors()) return;
    setSubmitting(true);
    try {
      const r = normalizeRole(userRole);
      const roleToUpdate = normalizeRole(formData.role);
      let url = "";

      if (r === "Owner" && roleToUpdate === "SubOwner") url = `${BASE_URL}/api/owner/subowners/${userId}`;
      else if ((r === "Owner" || r === "SubOwner") && roleToUpdate === "Vendor")
        url = `${BASE_URL}/api/owner/vendors/${userId}`;
      else if (r === "Vendor" && roleToUpdate === "Participant")
        url = `${BASE_URL}/api/vendor/participants/${userId}`;
      else throw new Error("Unauthorized");

      const payload = {
        name: capitalizeWords(formData.name.trim()),
        email: formData.email.trim().toLowerCase(),
        ...(formData.password ? { password: formData.password } : {}),
        ...(roleToUpdate === "SubOwner" ? { permissions: formData.permissions } : {}),
        ...(roleToUpdate === "Vendor" ? { domainType: formData.domainType } : {}),
        ...(roleToUpdate === "Participant" ? { locations } : {}),
      };

      await axios.put(url, payload, { headers: authHeaders });
      safeToast("User updated successfully", "success");
      closeForm();
      await fetchUsers(userRole, jwtToken, filterRole);
    } catch (err) {
      handleAxiosError(err, "Failed to update user");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (hasErrors()) return;
    selectedUserId ? handleUpdate(selectedUserId) : handleCreate();
  };

  const handleApprove = async (userId, role) => {
    setActionBusyId(userId);
    try {
      const r = normalizeRole(userRole);
      let url = "";
      if (r === "Owner" && normalizeRole(role) === "SubOwner") url = `${BASE_URL}/api/owner/appraove-subowner/${userId}`;
      else if (r === "Owner" && normalizeRole(role) === "Vendor") url = `${BASE_URL}/api/owner/approve-vendor/${userId}`;
      else if (r === "Vendor" && normalizeRole(role) === "Participant") url = `${BASE_URL}/api/vendor/approve-participant/${userId}`;
      else return;

      await axios.patch(url, {}, { headers: { Authorization: `Bearer ${jwtToken}` } });
      await fetchUsers(userRole, jwtToken, filterRole);
    } catch (err) {
      handleAxiosError(err);
    } finally {
      setActionBusyId(null);
    }
  };

  const handleUnapprove = async (userId, role) => {
    setActionBusyId(userId);
    try {
      const r = normalizeRole(userRole);
      let url = "";
      if (r === "Owner" && normalizeRole(role) === "SubOwner") url = `${BASE_URL}/api/owner/unapprove-subowner/${userId}`;
      else if (r === "Owner" && normalizeRole(role) === "Vendor") url = `${BASE_URL}/api/owner/unapprove-vendor/${userId}`;
      else if (r === "Vendor" && normalizeRole(role) === "Participant") url = `${BASE_URL}/api/vendor/unapprove-participant/${userId}`;
      else return;

      await axios.patch(url, {}, { headers: { Authorization: `Bearer ${jwtToken}` } });
      await fetchUsers(userRole, jwtToken, filterRole);
    } catch (err) {
      handleAxiosError(err);
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDelete = async (userId, role) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    setActionBusyId(userId);
    try {
      const r = normalizeRole(userRole);
      let url = "";
      if (r === "Owner" && normalizeRole(role) === "SubOwner") url = `${BASE_URL}/api/owner/subowners/${userId}`;
      else if (r === "Owner" && normalizeRole(role) === "Vendor") url = `${BASE_URL}/api/owner/vendors/${userId}`;
      else if (r === "Vendor" && normalizeRole(role) === "Participant") url = `${BASE_URL}/api/vendor/participants/${userId}`;
      else return;

      await axios.delete(url, { headers: { Authorization: `Bearer ${jwtToken}` } });
      safeToast("User deleted successfully", "success");
      await fetchUsers(userRole, jwtToken, filterRole);
    } catch (err) {
      handleAxiosError(err, "Failed to delete user");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleFilterChange = (e) => {
    setFilterRole(e.target.value);
    setCurrentPage(1);
  };

  const paginationHandlers = {
    prev: () => currentPage > 1 && setCurrentPage(p => p - 1),
    next: () => currentPage < totalPages && setCurrentPage(p => p + 1),
    go: (page) => page >= 1 && page <= totalPages && setCurrentPage(page),
  };

  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, 4, "...", totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages);
    }
    return pages;
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>User Management ({lsRoleLabel})</h1>
      <p className={styles.subtitle}>Add, update, or manage users with access to the system.</p>

      <div className={styles.topBar}>
        <input type="text" placeholder="Search by name, email, or role" className={styles.searchInput} readOnly />
        <div className={styles.filters}>
          {normalizeRole(userRole) === "Owner" && (
            <select className={styles.filterSelect} value={filterRole} onChange={handleFilterChange}>
              <option value="">All Roles</option>
              <option value="SubOwner">Sub Admin</option>
              <option value="Vendor">Client</option>
            </select>
          )}
        </div>
        <button className={styles.addButton} onClick={openAddForm}>
          + Add New User
        </button>
      </div>

      {loadingList ? (
        <div className={styles.noData}>Loading users...</div>
      ) : displayUsers.length === 0 ? (
        <div className={styles.noData}>No users found.</div>
      ) : (
        <>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayUsers.map((user) => {
                  const uRole = normalizeRole(user.role);
                  const canApprove = (normalizeRole(userRole) === "Owner" && ["SubOwner", "Vendor"].includes(uRole)) ||
                    (normalizeRole(userRole) === "Vendor" && uRole === "Participant");

                  return (
                    <tr key={user._id}>
                      <td>{user.name || "N/A"}</td>
                      <td>{user.email || "N/A"}</td>
                      <td>
                        <span className={`${styles.roleBadge} ${styles[uRole.toLowerCase()]}`}>
                          {getRoleDisplayName(uRole)}
                        </span>
                      </td>
                      <td className={styles.actionsCell}>
                        {canApprove && (
                          user.approved ? (
                            <button
                              onClick={() => handleUnapprove(user._id, uRole)}
                              className={`${styles.approveButton} ${styles.unapproveButton}`}
                              disabled={actionBusyId === user._id}
                            >
                              {actionBusyId === user._id ? "..." : "Unapprove"}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleApprove(user._id, uRole)}
                              className={styles.approveButton}
                              disabled={actionBusyId === user._id}
                            >
                              {actionBusyId === user._id ? "..." : "Approve"}
                            </button>
                          )
                        )}
                        <span className={styles.icon} onClick={() => handleEditClick(user)}><MdEdit /></span>
                        <span className={styles.icon} onClick={() => handleDelete(user._id, uRole)}><MdDelete /></span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.paginationContainer}>
              <div className={styles.paginationInfo}>
                Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, allUsers.length)} of {allUsers.length}
              </div>
              <div className={styles.paginationControls}>
                <button className={styles.submitButton} onClick={paginationHandlers.prev} disabled={currentPage === 1}>Previous</button>
                {getPageNumbers().map((p, i) => (
                  <button
                    key={i}
                    className={`${styles.pageNumber} ${p === currentPage ? styles.submitButton : ""} ${p === "..." ? styles.ellipsis : ""}`}
                    onClick={() => typeof p === "number" && paginationHandlers.go(p)}

                    disabled={p === "..."}
                  >
                    {p}
                  </button>
                ))}
                <button className={styles.submitButton} onClick={paginationHandlers.next} disabled={currentPage === totalPages}>Next</button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {showFormPopup && (
        <div className={styles.modalOverlay} onClick={closeForm}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 className={styles.formTitle}>
              {selectedUserId ? "Edit" : "Create"} {getRoleDisplayName(formData.role || getDefaultRoleForUser())}
            </h2>

            <div className={styles.formGrid}>
              <div>
                <label className={styles.label}>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Enter name"
                />
                {errors.name && <p className={styles.errorText}>{errors.name}</p>}
              </div>

              <div>
                <label className={styles.label}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={styles.input}
                  placeholder="Enter email"
                />
                {errors.email && <p className={styles.errorText}>{errors.email}</p>}
              </div>

              <div className={styles.passwordWrapper}>
                <label className={styles.label}>Password {selectedUserId && "(optional)"}</label>
                <div className={styles.passwordField}>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={styles.input}
                    placeholder={selectedUserId ? "Leave blank to keep current" : "Enter password"}
                  />
                  <span className={styles.eyeIcon} onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>
                {errors.password && <p className={styles.errorText}>{errors.password}</p>}
              </div>

              {(normalizeRole(userRole) === "Owner" || normalizeRole(userRole) === "SubOwner") && (
                <div>
                  <label className={styles.label}>Role</label>
                  <select name="role" value={formData.role} onChange={handleInputChange} className={styles.select}>
                    <option value="">Select Role</option>
                    {normalizeRole(userRole) === "Owner" && (
                      <>
                        <option value="SubOwner">Sub Admin</option>
                        <option value="Vendor">Client</option>
                      </>
                    )}
                    {normalizeRole(userRole) === "SubOwner" && <option value="Vendor">Client</option>}
                  </select>
                  {errors.role && <p className={styles.errorText}>{errors.role}</p>}
                </div>
              )}

              {(normalizeRole(userRole) === "Owner" || normalizeRole(userRole) === "SubOwner") &&
                normalizeRole(formData.role) === "Vendor" && (
                  <div>
                    <label className={styles.label}>Domain Type</label>
                    <select name="domainType" value={formData.domainType} onChange={handleInputChange} className={styles.select}>
                      <option value="">Select Domain</option>
                      <option>Real Estate</option>
                      <option>Recruiter</option>
                      <option>Health Care</option>
                      <option>Telecom</option>
                      <option>Election Party</option>
                    </select>
                    {errors.domainType && <p className={styles.errorText}>{errors.domainType}</p>}
                  </div>
                )}

              {normalizeRole(userRole) === "Vendor" && normalizeRole(formData.role) === "Participant" && (
                <div>
                  <label className={styles.label}>Locations (comma separated)</label>
                  <input
                    type="text"
                    value={locationsInput}
                    onChange={handleLocationsChange}
                    className={styles.input}
                    placeholder="e.g. New York, London, Tokyo"
                  />
                  {errors.locations && <p className={styles.errorText}>{errors.locations}</p>}
                </div>
              )}
            </div>

            {normalizeRole(userRole) === "Owner" && normalizeRole(formData.role) === "SubOwner" && (
              <div className={styles.permissionsContainer}>
                <label className={styles.label}>Permissions (comma separated)</label>
                <input
                  type="text"
                  value={formData.permissions.join(", ")}
                  onChange={(e) => {
                    const val = e.target.value;
                    setFormData(prev => ({ ...prev, permissions: val.split(",").map(p => p.trim()).filter(Boolean) }));
                  }}
                  className={styles.input}
                  placeholder="e.g. manageUsers, viewReports"
                />
              </div>
            )}

            <div className={styles.modalActions}>
              <button onClick={closeForm} className={styles.cancelButton} disabled={submitting}>
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className={styles.submitButton}
                disabled={submitting || hasErrors()}
              >
                {submitting ? "Saving..." : selectedUserId ? "Update User" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;